import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    ShoppingBag,
    Search,
    BadgePercent,
    Send,
} from "lucide-react";
import Footer from "../components/layout/Footer";
import { API_URL, getImageUrl } from "../services/api"; // 🔥 Importamos getImageUrl

const WHATSAPP_NUMBER = "5493412275598";
const FALLBACK_IMG = "/assets/smartpet-default.jpg";

// ─── Helpers ────────────────────────────────────────────────
const formatPrice = (value) => {
    const num = Number(value || 0);
    return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        maximumFractionDigits: 0,
    }).format(num);
};

const getWhatsappLink = (producto) => {
    const mensaje = `¡Hola! Quiero comprar este producto SmartPet:

Producto: ${producto.titulo}
Precio: ${formatPrice(producto.precio)}
${producto.descripcion ? `Descripción: ${producto.descripcion}` : ''}

Quiero personalizarlo con el nombre de mi mascota y el QR. ¿Cómo seguimos?`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensaje)}`;
};

// ─── Componente principal ──────────────────────────────────
export default function Comprar() {
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [filtro, setFiltro] = useState("todos");

    // ─── Establecer título de la página ────────────────────
    useEffect(() => {
        document.title = "Comprar | SmartPet";
    }, []);

    // ─── Cargar productos ──────────────────────────────────
    useEffect(() => {
        const fetchProductos = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_URL}/productos`);
                // ✅ La respuesta puede ser un array o un objeto con 'data'
                const data = res.data?.data || res.data || [];
                const activos = data
                    .filter(p => p.activo != 0)
                    .map(p => ({
                        id: p.id,
                        titulo: p.titulo || "Producto",
                        descripcion: p.descripcion || "",
                        precio: Number(p.precio) || 0,
                        oferta: Number(p.oferta) === 1,
                        imagen1: p.imagen1,
                        imagen2: p.imagen2,
                        imagen3: p.imagen3,
                    }));
                setProductos(activos);
            } catch (err) {
                console.error(err);
                setError("No pudimos cargar los productos. Intentá de nuevo.");
            } finally {
                setLoading(false);
            }
        };
        fetchProductos();
    }, []);

    // ─── Filtros ────────────────────────────────────────────
    const productosFiltrados = useMemo(() => {
        let result = productos;
        if (filtro === "oferta") {
            result = result.filter(p => p.oferta);
        }
        if (search.trim()) {
            const term = search.toLowerCase().trim();
            result = result.filter(p =>
                p.titulo.toLowerCase().includes(term) ||
                p.descripcion.toLowerCase().includes(term)
            );
        }
        return result;
    }, [productos, filtro, search]);

    const ofertas = productos.filter(p => p.oferta).slice(0, 4);

    // ─── Obtener la primera imagen del producto ──────────────
    const getFirstImage = (producto) => {
        const paths = [producto.imagen1, producto.imagen2, producto.imagen3].filter(Boolean);
        if (paths.length === 0) return FALLBACK_IMG;
        // ✅ Usamos getImageUrl para construir la URL completa
        return getImageUrl(paths[0], FALLBACK_IMG);
    };

    // ─── Render ─────────────────────────────────────────────
    if (loading) {
        return (
            <main className="min-h-screen bg-[#f4f1f9] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#6c5c94] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-[#6c5c94] font-bold">Cargando productos...</p>
                </div>
            </main>
        );
    }

    return (
        <>
            <main className="min-h-screen bg-[#f4f1f9] font-nunito">

                {/* ====== HERO ====== */}
                <section className="bg-[#4a3b7a] text-white py-12 md:py-20 px-4 text-center relative overflow-hidden">
                    <div className="max-w-4xl mx-auto">

                        <h1 className="text-3xl md:text-5xl font-black leading-tight">
                            Personalizá la <span className="text-[#f7c3dc]">identificación</span> de tu mascota
                        </h1>
                        <p className="text-white/80 text-lg font-semibold mt-4 max-w-2xl mx-auto">
                            Elegí el diseño, agregá el nombre y QR, y coordina tu compra por WhatsApp.
                            Sin suscripción, sin errores, solo tranquilidad.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4 mt-6">
                            <a href="#productos" className="bg-[#25D366] hover:bg-[#1eb858] px-8 py-3 rounded-full font-bold transition shadow-lg flex items-center gap-2">
                                <ShoppingBag size={20} /> Ver productos
                            </a>
                            <a href="#como-comprar" className="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-6 py-3 rounded-full font-bold transition">
                                ¿Cómo comprar?
                            </a>
                        </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-[#f4f1f9]" style={{ clipPath: 'ellipse(60% 100% at 50% 100%)' }} />
                </section>

                {/* ====== BENEFICIOS ====== */}
                <section className="max-w-6xl mx-auto px-4 py-10 -mt-4 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-md flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-[#ede8f9] flex items-center justify-center text-2xl flex-shrink-0">🎨</div>
                            <div>
                                <h3 className="font-bold text-[#2a1f3d]">Diseño único</h3>
                                <p className="text-sm text-[#555]">Elegí colores y nombre. Cada chapita es personalizada.</p>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-md flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-[#ede8f9] flex items-center justify-center text-2xl flex-shrink-0">📲</div>
                            <div>
                                <h3 className="font-bold text-[#2a1f3d]">Compra rápida</h3>
                                <p className="text-sm text-[#555]">Coordinamos todo por WhatsApp, sin intermediarios.</p>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-md flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-[#ede8f9] flex items-center justify-center text-2xl flex-shrink-0">♾️</div>
                            <div>
                                <h3 className="font-bold text-[#2a1f3d]">Sin suscripción</h3>
                                <p className="text-sm text-[#555]">Pagás una sola vez y el QR funciona de por vida.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ====== OFERTAS DESTACADAS ====== */}
                {ofertas.length > 0 && (
                    <section className="max-w-6xl mx-auto px-4 py-6">
                        <div className="flex items-center gap-2 mb-6">
                            <BadgePercent className="text-[#cd7fa7]" size={28} />
                            <h2 className="text-2xl font-black text-[#2a1f3d]">Ofertas especiales</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {ofertas.map(p => {
                                const img = getFirstImage(p);
                                return (
                                    <div key={p.id} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-[#eadff3] flex flex-col md:flex-row">
                                        <div className="md:w-2/5 h-48 md:h-auto bg-[#f7f2fa] flex items-center justify-center p-4">
                                            <img
                                                src={img}
                                                alt={p.titulo}
                                                className="w-full h-full object-contain"
                                                onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMG; }}
                                            />
                                        </div>
                                        <div className="p-6 md:w-3/5 flex flex-col justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="bg-[#cd7fa7] text-white text-xs font-bold px-2 py-0.5 rounded-full">OFERTA</span>
                                                </div>
                                                <h3 className="text-xl font-bold">{p.titulo}</h3>
                                                <p className="text-sm text-[#555] mt-1 line-clamp-2">{p.descripcion}</p>
                                                <p className="text-2xl font-black text-[#6c5c94] mt-2">{formatPrice(p.precio)}</p>
                                            </div>
                                            <a href={getWhatsappLink(p)} target="_blank" rel="noopener noreferrer" className="mt-4 bg-[#25D366] hover:bg-[#1eb858] text-white font-bold py-2.5 px-6 rounded-full transition flex items-center justify-center gap-2">
                                                <Send size={18} /> Comprar ahora
                                            </a>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* ====== LISTADO DE PRODUCTOS ====== */}
                <section id="productos" className="max-w-6xl mx-auto px-4 py-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                        <h2 className="text-2xl font-black text-[#2a1f3d] flex items-center gap-2">
                            <ShoppingBag size={28} className="text-[#6c5c94]" /> Todos los productos
                        </h2>
                        <div className="flex items-center gap-4 mt-4 md:mt-0">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888] w-4 h-4" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar..."
                                    className="pl-10 pr-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6c5c94] text-sm w-40 md:w-56"
                                />
                            </div>
                            <button
                                onClick={() => setFiltro(filtro === "oferta" ? "todos" : "oferta")}
                                className={`px-4 py-2 rounded-xl text-sm font-bold transition ${filtro === "oferta" ? "bg-[#cd7fa7] text-white" : "bg-white text-[#555] shadow-sm"}`}
                            >
                                {filtro === "oferta" ? "Todos" : "Ofertas"}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-xl mb-4">
                            {error}
                        </div>
                    )}

                    {productosFiltrados.length === 0 ? (
                        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                            <p className="text-[#888] font-semibold">No encontramos productos con esos filtros.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {productosFiltrados.map(p => {
                                const img = getFirstImage(p);
                                return (
                                    <div key={p.id} className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition border border-[#eadff3] flex flex-col">
                                        <div className="h-48 bg-[#f7f2fa] flex items-center justify-center p-3 relative">
                                            {p.oferta && (
                                                <span className="absolute top-2 left-2 bg-[#cd7fa7] text-white text-xs font-bold px-2 py-0.5 rounded-full">Oferta</span>
                                            )}
                                            <img
                                                src={img}
                                                alt={p.titulo}
                                                className="w-full h-full object-contain"
                                                onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMG; }}
                                            />
                                        </div>
                                        <div className="p-4 flex flex-col flex-1">
                                            <h3 className="font-bold text-[#2a1f3d] text-base line-clamp-1">{p.titulo}</h3>
                                            <p className="text-sm text-[#555] mt-1 line-clamp-2 flex-1">{p.descripcion}</p>
                                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#ede8f9]">
                                                <span className="text-xl font-black text-[#6c5c94]">{formatPrice(p.precio)}</span>
                                                <a href={getWhatsappLink(p)} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] hover:bg-[#1eb858] text-white text-sm font-bold py-1.5 px-4 rounded-full transition flex items-center gap-1">
                                                    <ShoppingBag size={16} /> Comprar
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* ====== CÓMO COMPRAR ====== */}
                <section id="como-comprar" className="max-w-6xl mx-auto px-4 py-12">
                    <div className="bg-white rounded-3xl p-8 md:p-12 shadow-md">
                        <div className="text-center mb-10">
                            <span className="inline-block bg-[#ede8f9] text-[#6c5c94] text-sm font-bold px-4 py-1 rounded-full">Fácil y rápido</span>
                            <h2 className="text-3xl font-black text-[#2a1f3d] mt-2">¿Cómo comprar tu SmartPet?</h2>
                            <p className="text-[#555] max-w-2xl mx-auto mt-2">En 3 pasos tenés tu chapita personalizada con QR.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-[#ede8f9] rounded-full flex items-center justify-center text-2xl mx-auto mb-4">1</div>
                                <h3 className="font-bold text-lg">Elegí tu diseño</h3>
                                <p className="text-sm text-[#555]">Seleccioná el modelo que más te guste y mirá sus imágenes.</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-[#ede8f9] rounded-full flex items-center justify-center text-2xl mx-auto mb-4">2</div>
                                <h3 className="font-bold text-lg">Comprá por WhatsApp</h3>
                                <p className="text-sm text-[#555]">Hacé clic en "Comprar" y coordinamos el diseño y el envío.</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-[#ede8f9] rounded-full flex items-center justify-center text-2xl mx-auto mb-4">3</div>
                                <h3 className="font-bold text-lg">Recibí tu chapita</h3>
                                <p className="text-sm text-[#555]">La preparamos con tu QR y la enviamos a tu domicilio.</p>
                            </div>
                        </div>
                        <div className="mt-8 text-center">
                            <a href="#productos" className="bg-[#6c5c94] hover:bg-[#4a3b7a] text-white font-bold px-8 py-3 rounded-full transition shadow-lg inline-flex items-center gap-2">
                                <ShoppingBag size={20} /> Ver productos
                            </a>
                        </div>
                    </div>
                </section>

            </main>
            <Footer />
        </>
    );
}